import { paginationService } from 'core/services/paginationService';
import { Holiday, HolidaysResponse } from 'types/holidays';

/**
 * Interface for fetching holidays parameters
 */
export interface FetchHolidaysParams {
	fromDate: string; // YYYY-MM-DD format
	toDate: string; // YYYY-MM-DD format
	itemsPerPage?: number;
	state?: string;
	country?: string;
}

/**
 * Fetch public holidays from the backend API
 * @param params - Parameters for fetching holidays including date range
 * @returns Promise with array of holidays
 */
export const fetchHolidays = async (params: FetchHolidaysParams): Promise<Holiday[]> => {
	const { fromDate, toDate, itemsPerPage = 100, state, country } = params;

	let queryString = `common/holiday_calendar?from_date=${fromDate}&to_date=${toDate}`;

	// Add optional filters
	if (state) {
		queryString += `&state=${state}`;
	}
	if (country) {
		queryString += `&country=${country}`;
	}

	try {
		const data = await paginationService.getWithHandler<HolidaysResponse>(
			queryString,
			undefined,
			itemsPerPage
		);

		// Extract holidays from the response
		const holidays = (data?.member || data?.['hydra:member'] || []) as Holiday[];
		return holidays;
	} catch (error) {
		console.error('Error fetching holidays:', error);
		return [];
	}
};

/**
 * Fetch a single holiday by ID
 * @param holidayId - The ID of the holiday to fetch
 * @returns Promise with holiday data
 */
export const fetchHolidayById = async (holidayId: number): Promise<Holiday | null> => {
	try {
		const { apiService } = await import('core/services/apiService');
		const response = await apiService.get(`common/holiday_calendar/${holidayId}`);
		return apiService.responseHandler(response);
	} catch (error) {
		console.error('Error fetching holiday by ID:', error);
		return null;
	}
};

/**
 * Fetch holidays for the current year
 * @returns Promise with array of holidays for current year
 */
export const fetchCurrentYearHolidays = async (): Promise<Holiday[]> => {
	const currentYear = new Date().getFullYear();
	const fromDate = `${currentYear}-01-01`;
	const toDate = `${currentYear}-12-31`;

	return fetchHolidays({ fromDate, toDate });
};
